import React, { useState, useEffect } from 'react'
import { Auth } from 'aws-amplify'
import { useAppDispatch, useAppSelector } from '../../../redux/storeExports'
import {
  postConfirmLogin,
  logOutUser,
  selectSessionExpirationTime,
} from 'shared/redux/authentication/authenticationSlice'
import {
  Button,
  Grid,
  Modal,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { theme } from '../../../theme/DashboardTheme'
import WarningIcon from 'shared/assets/icons/warningicon.svg'

interface SessionExpirationModalProps {
  open: boolean
  onClose: () => void
}

const SessionExpirationModal = ({ open, onClose }: SessionExpirationModalProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useAppDispatch()

  const expirationTime = useAppSelector(selectSessionExpirationTime)
  const [timeLeftString, setTimeLeftString] = useState('')

  useEffect(() => {
    if (!open || !expirationTime) return

    const updateTimer = () => {
      const currentTime = Math.floor(Date.now() / 1000)
      const timeLeft = expirationTime - currentTime

      if (timeLeft <= 0) {
        setTimeLeftString('00:00')
        return
      }

      const minutes = Math.floor(timeLeft / 60)
      const seconds = timeLeft % 60
      const formattedMinutes = String(minutes).padStart(2, '0')
      const formattedSeconds = String(seconds).padStart(2, '0')
      setTimeLeftString(`${formattedMinutes}:${formattedSeconds}`)
    }

    updateTimer()
    const timerInterval = setInterval(updateTimer, 1000)

    return () => clearInterval(timerInterval)
  }, [open, expirationTime])

  const handleStayLoggedIn = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true })
      await dispatch(postConfirmLogin(user)).unwrap()
      onClose()
    } catch (error) {
      onClose()
    }
  }

  const handleLogout = async () => {
    try {
      await dispatch(logOutUser()).unwrap()
      onClose()
    } catch (error) {
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      sx={{
        backdropFilter: 'blur(5px)',
        background: 'rgba(0, 0, 0, 0.01)',
      }}>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{
          outline: 'none',
          position: 'absolute',
          top: '50%',
          left: '50%',
          maxWidth: '600px',
          transform: 'translate(-50%, -50%)',
          width: !isMobile ? '30%' : '80%',
          background: '#fff',
          p: {xs: 5, sm: 6, md: 8},
          borderRadius: '10px',
        }}>
        <Stack justifyContent="center" alignItems="center" flexDirection={'column'}>
          <img src={WarningIcon} style={{  width: '8vw', maxWidth: '100px', minWidth: '60px', height: 'auto', }} />
          <Typography 
                variant="h2"
                mt={{xs: 2, sm: 2, md: 3, lg: 3}} 
                textAlign="center"
                sx={{
                    fontSize: {xs: 18, md: 21, lg: 23},
                    fontWeight: 600,
                    lineHeight: {xs: 1, md: 1.2, lg: 1.5},
                }}>
                Session Expiring
            </Typography>
          <Typography
            mt={2} 
            variant="body2" 
            textAlign="center" 
            color={'#615F6B'}
            sx={{
              fontSize: {xs: 12, md: 14, lg: 16},
              lineHeight: {xs: 1.2, md: 1.2, lg: 1.5},
              letterSpacing : '0'
            }}>
            Your session is about to expire. Would you like to stay logged in?
          </Typography>
        </Stack>
        <Grid item xs={12} sm={12} justifyContent="center" display="flex">
          <Button
            variant={'contained'}
            onClick={handleStayLoggedIn}
            sx={{
              width: {xs: '90%', sm: '100%', lg: '63%'},
              borderRadius: '10px',
              mt : 4,
              mb : 2,
              fontSize: {xs: 8, md: 13, lg: 14},
              boxShadow: 'unset',
              border: '2px solid #9135E0',
              background: '#9135E0',
              color: '#fff',
              '&.Mui-disabled': {
                background: '#9135E0',
                color: '#fff',
              },
              ':hover': {
                background: '#9135E0',
                color: '#fff',
              },
            }}>
            Stay Logged In {timeLeftString ? `(${timeLeftString})` : ''}
          </Button>
        </Grid>
        <Grid item xs={12} sm={12} justifyContent="center" display="flex">
          <Button
            variant={'contained'}
            color={'darkPurple'}
            onClick={handleLogout}
            sx={{
              width: {xs: '90%', sm: '100%', lg: '63%'},
              borderRadius: '10px',
              fontSize: {xs: 8, md: 13, lg: 14},
              boxShadow: 'unset',
              border: '2px solid #9135E0',
              background: 'transparent',
              color: '#9135E0',
              '&.Mui-disabled': {
                background: 'transparent',
                color: '#9135E0',
              },
              ':hover': {
                background: 'transparent',
                color: '#9135E0',
              },
            }}>
            Log Out
          </Button>
        </Grid>
      </Grid>
    </Modal>
  )
}

export default SessionExpirationModal